import { createPortal } from 'react-dom';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

import JxFormResizableBase from '../JxForms/JxFormResizableBase';
import JxTitleBar from '../JxTitleBar/JxTitleBar';

import { TITLE_BAR_ACTIONS } from './JxNoteTitleBarActions';
import jxNoteEngine from './JxNoteEngine';
import { jxScrollbarThin } from '../jxScrollbarStyle';
import userSettingsEngine from '../../controllers/UserSettings/UserSettingsEngine';
import { subscribe } from '../../services/utils/pubsub';
import { events } from '../../services/wss/SystemEvents';

import '../JxTheme.css';
import './JxNoteForm.css';

/**
 * JxNoteForm — post-it style note, same shape as JxStopwatchForm/JxCountdownForm.
 * No persistence — content only lives for the window's lifetime.
 * Multi-instance via JxWindowManager (openWindow assigns unique id).
 * @param {string} body - initial note text, editable via textarea
 */
export default class JxNoteForm extends JxFormResizableBase {
    constructor(props) {
        super(props);
        this.name = 'JxNoteForm';
        this.schema = {};
        this.state = { ...this.state, body: props.body ?? '', fontFamily: userSettingsEngine.resolve('note.fontFamily'), fontSize: userSettingsEngine.resolve('note.fontSize') };
    }

    _unsubscribeFontFamily = null;
    _unsubscribeFontSize = null;

    // Literal, hardcoded default — no window.innerWidth/innerHeight centering math.
    getDefaultGeo = () => ({ top: 300, left: 600, width: 200, height: 200 });

    componentDidMount() {
        super.componentDidMount();
        setTimeout(() => this.bringToTop(), 100);
        jxNoteEngine.save(this.props.id, this.state.body);

        // ADDED — live-update font when changed in SettingsTab, no refresh needed
        this._unsubscribeFontFamily = subscribe(events.onUserSettingChanged, (e) => {
            if (e.key !== 'note.fontFamily') return;
            this.setState({ fontFamily: e.value });
        });

        // ADDED — live-update font size when changed in SettingsTab, no refresh needed
        this._unsubscribeFontSize = subscribe(events.onUserSettingChanged, (e) => {
            if (e.key !== 'note.fontSize') return;
            this.setState({ fontSize: e.value });
        });
    }

    componentWillUnmount() {
        if (this._unsubscribeFontFamily) this._unsubscribeFontFamily = this._unsubscribeFontFamily();
        if (this._unsubscribeFontSize) this._unsubscribeFontSize = this._unsubscribeFontSize();
        super.componentWillUnmount();
    }

    handleBodyChange = (e) => {
        this.setState({ body: e.target.value });
        jxNoteEngine.save(this.props.id, e.target.value);
    };

    handleClose = () => {
        jxNoteEngine.remove(this.props.id);
        this.props.onClose();
    };

    // ADDED — Escape closes (and deletes) the note in the JxForm base by default; too easy to
    // hit by accident for a low-stakes post-it. Ignore it here, closing stays an explicit
    // titlebar-close-button action.
    _handleKeyDown = () => {};

    getTitleBarActions() {
        return [
            {
                icon: this.state.isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />,
                title: this.state.isPinned ? 'Unpin' : 'Pin on top',
                onClick: this.togglePin,
            },
            ...TITLE_BAR_ACTIONS(this.handleClose),
        ];
    }

    render() {
        if (!this.props.open) return null;

        return createPortal(
            <div
                {...this.panelProps}
                style={this.panelStyle}
                className={`jx-note-window ${this.panelActiveClass} ${this.panelPinnedClass}`}
            >
                <JxTitleBar
                    id={`${this.name}_${this.props.id}_titlebar`}
                    title="Note"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={this.getTitleBarActions()}
                />

                <div className="jx-note-window-body">
                    <textarea
                        id={`${this.name}_${this.props.id}_body`}
                        className={`jx-note-window-textarea ${jxScrollbarThin}`}
                        style={{ fontFamily: this.state.fontFamily, fontSize: this.state.fontSize }}
                        value={this.state.body}
                        onChange={this.handleBodyChange}
                        placeholder="Note…"
                        maxLength={500}
                    />
                </div>

                {this.renderResizeHandles()}
            </div>,
            document.body,
        );
    }
}
